import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField} from "@mui/material";
import PaperDraggable from "../../../../../components/PaperDraggable";
import { FormProvider, useForm } from "react-hook-form";
import {useMutation, useQueryClient} from "react-query";
import axiosClient from "../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import {useMemo, useState} from "react";

export interface IPraga {
    id?: number | null;
    nome_cientifico: string;
    nome_popular: string;
}

export interface DialogIncluirPragaProps {
    open: boolean;
    handleClose: () => void;
    defaultValue?: IPraga | null;
}

export default function DialogIncluirEditarPraga({
                                                     open,
                                                     handleClose,
                                                     defaultValue
                                                 }: DialogIncluirPragaProps) {
    const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const queryClient = useQueryClient();

    const { mutate: criarPraga, isLoading: isLoadingCriarPraga } = useMutation({
        mutationKey: "cadastrar-praga",
        mutationFn: (inputs: Omit<IPraga, "id">) =>
            axiosClient.post("/receituario-agronomico/pragas/adicionar-praga", inputs),
        onError: (error) => {
            enqueueSnackbar("Erro ao cadastrar praga!", { variant: "error" });
            setIsSubmitting(false);
        },
        onSuccess: () => {
            enqueueSnackbar("Praga cadastrada com sucesso!", { variant: "success" });
            queryClient.invalidateQueries("pesquisar-pragas");
            setIsSubmitting(false);
            handleClose();
        },
    });

    const { mutate: editarPraga, isLoading: isLoadingEditarPraga } = useMutation({
        mutationKey: "editar-praga",
        mutationFn: (inputs: IPraga) =>
            axiosClient.put(`/receituario-agronomico/pragas/editar-praga/${inputs.id}`, {
                nome_cientifico: inputs.nome_cientifico,
                nome_popular: inputs.nome_popular,
            }),
        onError: (error) => {
            enqueueSnackbar("Erro ao editar praga!", { variant: "error" });
            setIsSubmitting(false);
        },
        onSuccess: () => {
            enqueueSnackbar("Praga editada com sucesso!", { variant: "success" });
            queryClient.invalidateQueries("pesquisar-pragas");
            setIsSubmitting(false);
            handleClose();
        },
    });

    const valoresIniciais = useMemo(() => defaultValue ?? null, [defaultValue]);
    const methods = useForm<IPraga>({
        defaultValues: {
            id: valoresIniciais?.id ?? null,
            nome_cientifico: valoresIniciais?.nome_cientifico ?? "",
            nome_popular: valoresIniciais?.nome_popular ?? "",
        }
    });
    const { handleSubmit, reset } = methods;

    const onSubmit = (data: IPraga) => {
        setIsSubmitting(true);
        if (isEdit && data.id) {
            editarPraga(data);
        } else {
            criarPraga({
                nome_cientifico: data.nome_cientifico,
                nome_popular: data.nome_popular,
            });
        }
    };

    const handleCloseDialog = () => {
        reset();
        handleClose();
    };

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleCloseDialog}
                maxWidth="xs"
                fullWidth
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle id="draggable-dialog-title" style={{ cursor: 'move' }}>
                    {isEdit ? "Editar Praga" : "Cadastrar Praga"}
                </DialogTitle>
                <DialogContent>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            label="Nome Científico"
                            fullWidth
                            autoFocus
                            {...methods.register("nome_cientifico", { required: "O nome científico é obrigatório" })}
                            disabled={isSubmitting}
                            margin="normal"
                        />
                        <TextField
                            label="Nome Popular"
                            fullWidth
                            {...methods.register("nome_popular", { required: "O nome popular é obrigatório" })}
                            disabled={isSubmitting}
                            margin="normal"
                        />
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="text"
                        onClick={handleCloseDialog}
                        disabled={isSubmitting}
                    >
                        Fechar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoadingEditarPraga || isLoadingCriarPraga}
                    >
                        {(isLoadingEditarPraga || isLoadingCriarPraga) && (
                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                        )}
                        {isEdit ? "Atualizar" : "Cadastrar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </FormProvider>
    );
}